'use client'

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from 'react'
import { FaPause, FaPlay, FaRedoAlt } from 'react-icons/fa'
import { Icon } from 'src/components/site/icons'
import { CropImage, TabPill } from 'src/components/showcase/crop-image'
import {
  FEATURES,
  IMAGE,
  SCREEN,
  TAB_FEATURE_ID,
  TIMING,
  focusRect,
  type Rect,
} from 'src/components/showcase/showcase-config'
import { useMediaQuery, useReducedMotion } from 'src/components/site/use-media-query'

type Mode = 'idle' | 'tour' | 'held' | 'done'
type Phase = 'show' | 'gap'

type Layout = {
  w: number
  h: number
  sx: number
  sy: number
  sw: number
  sh: number
  btn: { x: number; y: number; w: number }[]
}

const OUTLINE_PAD = 3

const rectPath = (r: Rect) => {
  const x = r.x * IMAGE.width
  const y = r.y * IMAGE.height
  const w = r.w * IMAGE.width
  const h = r.h * IMAGE.height
  return `M${x} ${y}h${w}v${h}h${-w}z`
}

export const FeatureShowcase = () => {
  const wrapRef = useRef<HTMLDivElement>(null)
  const stageRef = useRef<HTMLDivElement>(null)
  const btnRefs = useRef<(HTMLButtonElement | null)[]>([])

  const reduced = useReducedMotion()
  const isMobile = useMediaQuery('(max-width: 639px)')
  const lifts = !reduced && !isMobile

  const [layout, setLayout] = useState<Layout | null>(null)
  const [inView, setInView] = useState(false)
  const [entered, setEntered] = useState(false)

  const [mode, setMode] = useState<Mode>('idle')
  const [phase, setPhase] = useState<Phase>('show')
  const [tourIdx, setTourIdx] = useState(-1)
  const [playing, setPlaying] = useState(true)
  const [selected, setSelected] = useState<number | null>(null)
  const [hover, setHover] = useState<number | null>(null)
  const [flip, setFlip] = useState(false)
  const [capIdx, setCapIdx] = useState<number | null>(null)

  /* Which feature is on screen right now */
  const current =
    mode === 'held'
      ? selected
      : mode === 'tour' && phase === 'show' && tourIdx >= 0
        ? tourIdx
        : null
  const shown = hover ?? current

  /* Keep the previous caption while a tour step returns, clear it once the tour ends */
  if (shown !== null && shown !== capIdx) setCapIdx(shown)
  if (mode === 'done' && shown === null && capIdx !== null) setCapIdx(null)

  /* ---------- measurement (stage + button anchors, aligned on resize) ---------- */
  const measure = useCallback(() => {
    const wrap = wrapRef.current
    const stage = stageRef.current
    if (!wrap || !stage) return
    const section = wrap.closest('section')
    if (section) {
      // Reserve space for the heading, caption, controls and mobile close-up.
      const overhead = section.getBoundingClientRect().height - stage.offsetHeight
      const viewportHeight = window.visualViewport?.height ?? window.innerHeight
      const minimum = window.innerWidth < 640 ? 120 : 180
      const available = Math.max(minimum, Math.floor(viewportHeight - overhead - 32))
      const height = `${available}px`
      if (wrap.style.getPropertyValue('--tour-stage-height') !== height) {
        wrap.style.setProperty('--tour-stage-height', height)
      }
    }
    const next: Layout = {
      w: wrap.offsetWidth,
      h: wrap.offsetHeight,
      sx: stage.offsetLeft,
      sy: stage.offsetTop,
      sw: stage.offsetWidth,
      sh: stage.offsetHeight,
      btn: btnRefs.current.map((b) =>
        {return b ? { x: b.offsetLeft, y: b.offsetTop, w: b.offsetWidth } : { x: 0, y: 0, w: 0 }},
      ),
    }
    setLayout((prev) => {return (prev && JSON.stringify(prev) === JSON.stringify(next) ? prev : next)})
  }, [])

  useLayoutEffect(() => {
    measure()
    const ro = new ResizeObserver(measure)
    if (wrapRef.current) ro.observe(wrapRef.current)
    if (stageRef.current) ro.observe(stageRef.current)
    document.fonts?.ready.then(measure)
    window.addEventListener('resize', measure)
    window.visualViewport?.addEventListener('resize', measure)
    return () => {
      ro.disconnect()
      window.removeEventListener('resize', measure)
      window.visualViewport?.removeEventListener('resize', measure)
    }
  }, [measure])

  /* ---------- viewport visibility ---------- */
  useEffect(() => {
    const el = wrapRef.current
    if (!el) return
    const io = new IntersectionObserver(
      ([entry]) => {
        setInView(entry.isIntersecting)
        if (entry.isIntersecting) setEntered(true)
      },
      { threshold: 0.3 },
    )
    io.observe(el)
    return () => {return io.disconnect()}
  }, [])

  /* ---------- guided tour ---------- */
  const startTour = useCallback(() => {
    setSelected(null)
    setPlaying(true)
    setTourIdx(0)
    setPhase('show')
    setMode('tour')
  }, [])

  useEffect(() => {
    if (!entered || !inView || reduced || mode !== 'idle') return
    const id = setTimeout(startTour, TIMING.introDelay)
    return () => {return clearTimeout(id)}
  }, [entered, inView, reduced, mode, startTour])

  useEffect(() => {
    if (reduced && mode === 'tour') setMode('done')
  }, [reduced, mode])

  useEffect(() => {
    if (mode !== 'tour' || !playing || !inView || hover !== null) return
    const id = setTimeout(
      () => {
        if (phase === 'show') {
          setPhase('gap')
        } else if (tourIdx < FEATURES.length - 1) {
          setTourIdx(tourIdx + 1)
          setPhase('show')
        } else {
          setTourIdx(-1)
          setPhase('show')
          setMode('done')
        }
      },
      phase === 'show' ? TIMING.hold : TIMING.gap,
    )
    return () => {return clearTimeout(id)}
  }, [mode, playing, inView, hover, phase, tourIdx])

  /* ---------- tab indicator switching ---------- */
  const tabsShown = shown !== null && FEATURES[shown].id === TAB_FEATURE_ID
  useEffect(() => {
    if (!tabsShown || reduced) {
      setFlip(false)
      return
    }
    const id = setInterval(() => {return setFlip((f) => {return !f})}, TIMING.tabSwitch)
    return () => {return clearInterval(id)}
  }, [tabsShown, reduced])

  /* ---------- interaction ---------- */
  const select = (i: number) => {
    if (mode === 'held' && selected === i) {
      setSelected(null)
      setMode('done')
    } else {
      setSelected(i)
      setMode('held')
    }
  }

  /* ---------- derived geometry ---------- */
  const connectors = useMemo(() => {
    if (!layout) return []
    const stageBottom = layout.sy + layout.sh
    return FEATURES.map((f, i) => {
      let left = Infinity
      let right = -Infinity
      let bottom = -Infinity
      for (const p of f.parts) {
        const cx = layout.sx + (p.rect.x + p.rect.w / 2) * layout.sw + p.lift.dx * layout.sw
        const cy = layout.sy + (p.rect.y + p.rect.h / 2) * layout.sh + p.lift.dy * layout.sw
        const hw = (p.rect.w * layout.sw * p.lift.scale) / 2
        const hh = (p.rect.h * layout.sh * p.lift.scale) / 2
        left = Math.min(left, cx - hw)
        right = Math.max(right, cx + hw)
        bottom = Math.max(bottom, cy + hh)
      }
      const ax = f.anchorX !== undefined ? layout.sx + f.anchorX * layout.sw : (left + right) / 2
      const b = layout.btn[i] ?? { x: 0, y: 0, w: 0 }
      const bx = b.x + b.w / 2
      const by = b.y
      const midY = Math.max(bottom + 12, stageBottom + (by - stageBottom) / 2)
      return { d: `M${ax} ${bottom}V${midY}H${bx}V${by - 4}`, bx, by }
    })
  }, [layout])

  const dimPaths = FEATURES.map((f) => {
    const holes = f.hole === false && lifts ? [] : f.parts.map((p) => {return p.rect})
    return rectPath(SCREEN) + holes.map(rectPath).join('')
  })

  const capFeature = capIdx !== null ? FEATURES[capIdx] : null

  return (
    <div
      ref={wrapRef}
      className="tour"
      data-entered={entered ? 'true' : 'false'}
      role="group"
      aria-label="Interactive tour of WerxPace features"
    >
      {/* Caption + tour controls */}
      <div className="cap">
        <div className="cap-text" role="status" aria-live="polite" aria-atomic="true">
          {capFeature ? (
            <p key={capFeature.id}>
              <span className="eyebrow">{capFeature.label}</span>
              {capFeature.caption}
            </p>
          ) : (
            <p key="hint" className="hint">
              {reduced || mode === 'done'
                ? 'Select a feature to see where it lives in the app.'
                : 'Hover or tap a feature to see where it lives in the app.'}
            </p>
          )}
        </div>

        {!reduced && mode === 'tour' && (
          <button
            type="button"
            className="tctl"
            aria-pressed={!playing}
            onClick={() => {return setPlaying((p) => {return !p})}}
          >
            {playing ? <FaPause aria-hidden="true" /> : <FaPlay aria-hidden="true" />}
            {playing ? 'Pause tour' : 'Resume tour'}
          </button>
        )}
        {!reduced && (mode === 'done' || mode === 'held') && (
          <button type="button" className="tctl" onClick={startTour}>
            <FaRedoAlt aria-hidden="true" />
            Replay tour
          </button>
        )}
      </div>

      {/* Laptop + overlays */}
      <div ref={stageRef} className="stage">
        <img
          className="base"
          src={IMAGE.src}
          width={IMAGE.width}
          height={IMAGE.height}
          alt="WerxPace workspace showing a video call, channels and chat on a laptop"
          draggable={false}
        />

        <svg
          className="dim"
          viewBox={`0 0 ${IMAGE.width} ${IMAGE.height}`}
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          {FEATURES.map((f, i) => {return (
            <path key={f.id} d={dimPaths[i]} data-on={shown === i} />
          )})}
        </svg>

        {layout ? <svg
            className="outlines"
            viewBox={`0 0 ${layout.sw} ${layout.sh}`}
            aria-hidden="true"
          >
            {FEATURES.map((f, i) =>
              {return f.parts.map((p) => {return (
                <rect
                  key={`${f.id}-${p.rect.x}-${p.rect.y}`}
                  x={p.rect.x * layout.sw - OUTLINE_PAD}
                  y={p.rect.y * layout.sh - OUTLINE_PAD}
                  width={p.rect.w * layout.sw + OUTLINE_PAD * 2}
                  height={p.rect.h * layout.sh + OUTLINE_PAD * 2}
                  rx={4}
                  pathLength={1}
                  data-on={shown === i}
                />
              )})},
            )}
          </svg> : null}

        {lifts ? FEATURES.map((f, i) =>
            {return f.parts.map((p) => {
              const on = shown === i
              const sw = layout?.sw ?? 0
              return (
                <div
                  key={`${f.id}-${p.rect.x}-${p.rect.y}`}
                  className="crop"
                  data-on={on}
                  aria-hidden="true"
                  style={{
                    left: `${p.rect.x * 100}%`,
                    top: `${p.rect.y * 100}%`,
                    width: `${p.rect.w * 100}%`,
                    height: `${p.rect.h * 100}%`,
                    transform: on
                      ? `translate3d(${p.lift.dx * sw}px, ${p.lift.dy * sw}px, 0) scale(${p.lift.scale})`
                      : undefined,
                  }}
                >
                  <CropImage rect={p.rect} />
                  {p.indicator ? <TabPill rect={p.rect} indicator={p.indicator} isFlipped={flip} /> : null}
                </div>
              )
            })},
          ) : null}
      </div>

      {/* Connectors from the lifted region to its button */}
      {lifts && layout ? <svg
          className="lines"
          viewBox={`0 0 ${layout.w} ${layout.h}`}
          aria-hidden="true"
        >
          {connectors.map((c, i) => {return (
            <g key={FEATURES[i].id}>
              <path d={c.d} pathLength={1} data-on={shown === i} />
              <circle cx={c.bx} cy={c.by} r={3.5} data-on={shown === i} />
            </g>
          )})}
        </svg> : null}

      {/* Feature buttons */}
      <ul className="fgrid">
        {FEATURES.map((f, i) => {return (
          <li key={f.id} className="fli" style={{ '--i': i } as CSSProperties}>
            <button
              ref={(el) => {
                btnRefs.current[i] = el
              }}
              type="button"
              className="fbtn"
              data-active={current === i}
              data-preview={hover === i && current !== i}
              aria-pressed={mode === 'held' && selected === i}
              onClick={() => {return select(i)}}
              onPointerEnter={(e) => {
                if (e.pointerType === 'mouse') setHover(i)
              }}
              onPointerLeave={(e) => {
                if (e.pointerType === 'mouse') setHover(null)
              }}
              onFocus={(e) => {
                if (e.currentTarget.matches(':focus-visible')) setHover(i)
              }}
              onBlur={() => {return setHover((h) => {return (h === i ? null : h)})}}
            >
              <Icon name={f.icon} />
              <span>{f.label}</span>
            </button>
          </li>
        )})}
      </ul>

      {/* Mobile: enlarged crop below the buttons */}
      <div className="zoom">
        {shown !== null ? (
          (() => {
            const f = FEATURES[shown]
            const fr = focusRect(f)
            const ratio = (fr.w * IMAGE.width) / (fr.h * IMAGE.height)
            const indicator = f.parts[0].indicator
            return (
              <div
                key={f.id}
                className="zoomcrop"
                aria-hidden="true"
                style={{
                  aspectRatio: ratio,
                  // Match v0: fit the complete region within the mobile preview.
                  width: `min(100%, ${fr.w * IMAGE.width * 1.7}px, ${270 * ratio}px)`,
                }}
              >
                <CropImage rect={fr} />
                {indicator ? <TabPill rect={fr} indicator={indicator} isFlipped={flip} /> : null}
              </div>
            )
          })()
        ) : (
          <p className="cap-text hint" style={{ margin: 0 }}>
            Tap a feature to see where it lives in the app.
          </p>
        )}
      </div>


    </div>
  )
}
